import { useAuth } from '@/features/authentication';
import { cn } from '@/shared/lib';
import { Button } from '@/shared/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/shared/ui/sheet';
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  Camera,
  LayoutDashboard,
  Menu,
  MessageSquare,
  Shield,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

const adminNavItems = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/users', label: 'Utilisateurs', icon: Users },
  { href: '/admin/photos', label: 'Photos', icon: Camera },
  { href: '/admin/meetings', label: 'Rendez-vous', icon: Calendar },
  { href: '/admin/questions', label: 'Questions', icon: MessageSquare },
  { href: '/admin/stats', label: 'Statistiques', icon: BarChart3 },
];

/** /admin is exact; sections stay active on their sub-pages (/admin/users/123). */
function isActive(pathname: string, href: string) {
  return href === '/admin' ? pathname === href : pathname.startsWith(href);
}

function AdminNav({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }) {
  return (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto p-4">
        {adminNavItems.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={onNavigate}
            aria-current={isActive(pathname, item.href) ? 'page' : undefined}
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 transition-colors',
              isActive(pathname, item.href)
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'hover:bg-sidebar-accent/50',
            )}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="border-t border-sidebar-border p-4">
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="flex items-center gap-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour au site
        </Link>
      </div>
    </>
  );
}

/// Layout for the admin route group. Requires an authenticated admin, otherwise
/// redirects to /dashboard. Desktop: fixed-width sidebar pinned to the viewport
/// (it no longer stretches or scrolls with the page). Mobile: top bar + drawer.
export function AdminLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isLoading, isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/dashboard', { replace: true });
    }
  }, [isLoading, isAuthenticated, isAdmin, navigate]);

  if (isLoading || !isAdmin) {
    return null;
  }

  const brand = (
    <Link to="/admin" className="flex items-center gap-2">
      <Shield className="h-6 w-6 text-sidebar-primary" />
      <span className="text-lg font-bold">Admin</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-background lg:flex">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:flex">
        <div className="border-b border-sidebar-border p-4">{brand}</div>
        <AdminNav pathname={pathname} />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between bg-sidebar px-4 py-3 text-sidebar-foreground lg:hidden">
        {brand}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-sidebar-foreground hover:bg-sidebar-accent"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="flex w-64 flex-col gap-0 bg-sidebar p-0 text-sidebar-foreground"
          >
            <SheetTitle className="border-b border-sidebar-border p-4 text-sidebar-foreground">
              Admin
            </SheetTitle>
            <AdminNav pathname={pathname} onNavigate={() => setMenuOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>

      <main className="min-w-0 flex-1">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
